import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createPromoShopItem, getPromoById, getPromoShopItems } from '../../../apis/atlantis.api';
import { useAuth } from '../../../contexts/auth.context';
import ExpandableShopItem from './ShopItem';
import AddShopItemButton from './AddShopItemButton';
import PromoBreadcrumb from './PromoBreadcrumb';
import { formatDate, getPromoTypeColor, getStatusColor } from '../../terminal/handlers/utils';
import { PromoType, VipTierLabel, VipTierThresholdsSol } from '../../../utils/constants';
import { Button, Spinner } from 'react-bootstrap';

const Promo = () => {
  const navigate = useNavigate();

  const [promo, setPromo] = useState(null);
  const [shopItems, setShopItems] = useState([]);
  const { promoId } = useParams();
  const { auth } = useAuth();
  const [loading, setLoading] = useState(false);

  const authToken = useRef(auth?.idToken); // Create a ref for the auth?.idToken

  useEffect(() => {
    authToken.current = auth?.idToken; // Update the ref with the latest value of auth?.idToken
  }, [auth?.idToken]);

  useEffect(() => {
    const fetchPromo = async () => {
      try {
        const promoData = await getPromoById(promoId, authToken.current);
        setPromo(promoData);
      } catch (error) {
        console.error('Error fetching promo data:', error);
      }
    };

    fetchPromo();
  }, [promoId]);

  const fetchPromoShopItems = useCallback(async () => {
    setLoading(true);
    try {
      const items = await getPromoShopItems(promoId, authToken.current);
      setShopItems(items);
    } catch (error) {
      console.error('Error fetching promo shop items:', error);
    }
    setLoading(false);
  }, [promoId]);

  useEffect(() => {
    fetchPromoShopItems();
    if ([PromoType.FRAGMENT, PromoType.LOSER].includes(promo?.promoType)) {
      fetchPromoShopItems();
    }
  }, [promo, fetchPromoShopItems]);

  const handleRefresh = async () => {
    setLoading(true);
    await fetchPromoShopItems();
    setLoading(false);
  };

  const handleAddShopItem = async newShopItem => {
    newShopItem.promoId = promo.promoId;
    const newItem = await createPromoShopItem(promo.promoId, newShopItem, authToken.current);
    setShopItems([...shopItems, newItem]);
  };

  const LoadingSpinner = () => (
    <Spinner animation="border" size="sm" role="status">
      <span className="visually-hidden">Loading...</span>
    </Spinner>
  );

  const isVip = promo?.promoType === PromoType.VIP;
  const isManageable = [PromoType.FRAGMENT, PromoType.LOSER, PromoType.VIP].includes(
    promo?.promoType
  );

  // Tier-themed colors for the qualifying-volume callout: Big Degen = gold, VVIP = purple.
  const vipTierTheme = {
    BD: { bg: '#fdf6e3', border: '#f0e0b0', accent: '#b8860b' },
    VVIP: { bg: '#eef2ff', border: '#e0e7ff', accent: '#4338ca' },
  }[promo?.vipTier] ?? { bg: '#f1f3f5', border: '#dee2e6', accent: '#495057' };

  return (
    <div className="container pt-3" style={{ overflow: 'scroll' }}>
      {promo ? (
        <div>
          <PromoBreadcrumb currentName={promo?.promoName} promoId={promo.promoId} />
          <div className="card border-0 shadow-sm rounded-4 my-4">
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                <div>
                  <h2 className="mb-2 fw-semibold text-dark">{promo.promoName}</h2>
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span className={`badge ${getPromoTypeColor(promo.promoType)}`}>
                      {promo.promoType}
                    </span>
                    <span className={`badge ${getStatusColor(promo.status)}`}>{promo.status}</span>
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                    <i className="fa-solid fa-calendar me-2" />
                    {formatDate(promo.startTime)} &ndash; {formatDate(promo.endTime)}
                  </div>
                </div>
                {isVip && promo.vipTier && (
                  <div
                    className="rounded-4 px-4 py-3 text-center"
                    style={{
                      background: vipTierTheme.bg,
                      border: `1px solid ${vipTierTheme.border}`,
                      minWidth: '180px',
                    }}
                  >
                    <div
                      className="text-uppercase text-muted fw-semibold mb-1"
                      style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}
                    >
                      Qualifying Volume
                    </div>
                    <div
                      className="fw-bold"
                      style={{ fontSize: '1.6rem', color: vipTierTheme.accent, lineHeight: 1.1 }}
                    >
                      {VipTierThresholdsSol[promo.vipTier] ?? '—'}{' '}
                      <span style={{ fontSize: '1rem' }}>SOL</span>
                    </div>
                    <div className="mt-2">
                      <span
                        className="badge rounded-pill"
                        style={{
                          background: vipTierTheme.accent,
                          color: '#fff',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          padding: '0.4em 0.85em',
                          letterSpacing: '0.02em',
                        }}
                      >
                        {VipTierLabel[promo.vipTier] ?? promo.vipTier} Tier
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <LoadingSpinner />
      )}
      {promo && isManageable && (
        <>
          <div className="d-flex justify-content-between mb-2">
            <h4>{isVip ? 'Reward Items' : 'Shop Items'}</h4>
            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-dark me-2"
                onClick={() => navigate(`/promos/${promoId}/search`)}
              >
                <i className="fa-solid fa-search" /> Search Wallet
              </button>
              <AddShopItemButton onAdd={handleAddShopItem} token={auth?.idToken} isVip={isVip} />
              <Button
                variant="secondary"
                className="mx-2"
                onClick={handleRefresh}
                disabled={loading}
              >
                {loading ? <LoadingSpinner /> : 'Refresh'}
              </Button>
            </div>
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : shopItems.length > 0 ? (
            <div>
              {shopItems.map(item => (
                <ExpandableShopItem
                  key={item.itemId}
                  shopItem={item}
                  token={auth?.idToken}
                  refreshShopItems={fetchPromoShopItems}
                  isVip={isVip}
                />
              ))}
            </div>
          ) : (
            <p className="text-muted">
              No {isVip ? 'reward' : 'shop'} items yet. Add one to define the reward.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default Promo;
